/**
 * Stepper Component
 *
 * The one step indicator for every multi-step flow (sign-up, list your property,
 * post a request, membership, 2FA). See Apps/web/THEME.md §2.5.
 *
 * Layout (Monsha'at style): an <ol> of EQUAL-WIDTH cells. Each cell holds a
 * before-half line, the marker and an after-half line, so the connectors always run
 * through the marker centres (the StageTrack pattern from
 * components/market-ui/charts.tsx) no matter how many steps there are or how wide
 * the screen is. Only the current step shows its label, in a pill under it, which is
 * what keeps eight steps legible on a 360px phone. «الخطوة 2 من 5» sits above.
 *
 * Markers are <span>s, never <button>s: the global `button { min-height: 44px }`
 * rule for small screens (index.css) used to stretch them into ovals. Where earlier
 * steps are clickable (the register wizard), a `min-h-0` button wraps the marker.
 *
 *   completed: solid teal circle, white check
 *   current:   white circle, 2px teal ring, soft halo, teal numeral
 *   upcoming:  #DDEEEB circle, muted numeral
 *
 * Screen readers get an ordered list with `aria-current="step"`, a full sentence per
 * step («الخطوة 2: الموقع، الحالية»), and a polite live line that announces each move.
 */

import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cx, useDirection, useUiText } from './primitiveUtils';

export interface Step {
  id: string;
  label?: string;
  description?: string;
  icon?: string;
}

export interface StepperProps {
  steps: Step[];
  /** 0-based index of the step the user is on. */
  currentStep: number;
  variant?: 'default' | 'compact' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  showLabels?: boolean;
  /** Visible «الخطوة 2 من 5». Default true. */
  showCounter?: boolean;
  /** Names the step list for screen readers. Default «خطوات العملية». */
  ariaLabel?: string;
  onStepClick?: (stepIndex: number) => void;
  /** Let the user jump back to completed steps (requires `onStepClick`). */
  allowClickPrevious?: boolean;
  className?: string;
}

type Status = 'completed' | 'current' | 'upcoming';

const SIZE = {
  sm: { marker: 'h-6 w-6', glyph: 'text-[0.75rem]', hit: '-m-2.5 p-2.5', line: 'w-4' },
  md: { marker: 'h-8 w-8', glyph: 'text-[1rem]', hit: '-m-1.5 p-1.5', line: 'w-6' },
  lg: { marker: 'h-10 w-10', glyph: 'text-[1.25rem]', hit: '-m-1 p-1', line: 'w-8' },
} as const;

const TEAL = 'bg-[var(--pv-color-primary)]';
const SOFT = 'bg-[#DDEEEB] dark:bg-[var(--pv-color-surface-soft)]';

const MARKER_STATE: Record<Status, string> = {
  completed: 'bg-[var(--pv-color-primary)] text-white dark:text-[var(--pv-color-surface)]',
  current:
    'bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-primary)] ring-2 ring-[var(--pv-color-primary)] ' +
    'shadow-[0_0_0_4px_rgba(0,77,64,0.12)] dark:shadow-[0_0_0_4px_rgba(63,187,162,0.2)]',
  upcoming: 'bg-[#DDEEEB] text-[var(--pv-color-text-muted)] dark:bg-[var(--pv-color-surface-soft)]',
};

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--pv-color-primary)]';

export default function Stepper({
  steps,
  currentStep,
  variant = 'default',
  size = 'md',
  showLabels = true,
  showCounter = true,
  ariaLabel,
  onStepClick,
  allowClickPrevious = false,
  className = '',
}: StepperProps) {
  const tx = useUiText();
  const dir = useDirection();
  const total = steps.length;
  // Past the end means "all done"; below zero means "not started".
  const current = Math.min(Math.max(currentStep, -1), total);
  const sz = SIZE[size];

  const statusOf = (index: number): Status =>
    index < current ? 'completed' : index === current ? 'current' : 'upcoming';

  const statusText = (status: Status) =>
    status === 'completed'
      ? tx('stepper.status.completed', 'completed')
      : status === 'current'
        ? tx('stepper.status.current', 'current')
        : tx('stepper.status.upcoming', 'upcoming');

  const describe = (step: Step, index: number) => {
    const status = statusText(statusOf(index));
    return step.label
      ? tx('stepper.stepWithLabel', 'Step {{n}}: {{label}}, {{status}}', { n: index + 1, label: step.label, status })
      : tx('stepper.stepNoLabel', 'Step {{n}}, {{status}}', { n: index + 1, status });
  };

  const isClickable = (index: number) => allowClickPrevious && !!onStepClick && index < current;

  const shown = Math.min(Math.max(current + 1, 1), total);
  const counter = tx('stepper.counter', 'Step {{current}} of {{total}}', { current: shown, total });
  const currentLabel = current >= 0 && current < total ? steps[current].label : undefined;
  const live = currentLabel
    ? tx('stepper.counterWithLabel', 'Step {{current}} of {{total}}: {{label}}', {
        current: shown,
        total,
        label: currentLabel,
      })
    : counter;

  const renderMarker = (step: Step, index: number): ReactNode => {
    const status = statusOf(index);
    const glyph =
      status === 'completed' ? (
        <i className="ri-check-line" />
      ) : step.icon ? (
        <i className={step.icon} />
      ) : (
        index + 1
      );
    const marker = (
      <span
        aria-hidden="true"
        data-status={status}
        className={cx(
          'relative inline-flex shrink-0 select-none items-center justify-center rounded-full font-medium leading-none tabular-nums transition-colors duration-200',
          sz.marker,
          sz.glyph,
          MARKER_STATE[status],
        )}
      >
        {glyph}
      </span>
    );
    if (!isClickable(index)) return marker;
    const goTo = step.label
      ? tx('stepper.goTo', 'Back to step {{n}}: {{label}}', { n: index + 1, label: step.label })
      : tx('stepper.goToNoLabel', 'Back to step {{n}}', { n: index + 1 });
    return (
      <button
        type="button"
        onClick={() => onStepClick?.(index)}
        aria-label={goTo}
        className={cx(
          'relative z-10 inline-flex min-h-0 shrink-0 cursor-pointer items-center justify-center rounded-full bg-transparent',
          'transition-transform duration-150 hover:scale-105',
          sz.hit,
          FOCUS_RING,
        )}
      >
        {marker}
      </button>
    );
  };

  if (total === 0) return null;

  const heading = (
    <>
      {showCounter && (
        <p
          className={cx(
            'text-[0.875rem] leading-snug text-[var(--pv-color-text-muted)]',
            variant === 'compact' ? 'mb-2 text-center' : 'mb-3 text-start',
          )}
          aria-hidden="true"
        >
          {counter}
        </p>
      )}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {live}
      </p>
    </>
  );

  // ------------------------------------------------------------------ compact
  if (variant === 'compact') {
    return (
      <nav aria-label={ariaLabel ?? tx('stepper.label', 'Steps')} className={cx('w-full', className)} dir={dir}>
        {heading}
        <ol className="flex items-center justify-center" dir={dir}>
          {steps.map((step, index) => (
            <li
              key={step.id}
              className="flex items-center"
              aria-current={index === current ? 'step' : undefined}
            >
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={cx('h-0.5 shrink-0', sz.line, index <= current ? TEAL : SOFT)}
                />
              )}
              {renderMarker(step, index)}
              <span className="sr-only">{describe(step, index)}</span>
            </li>
          ))}
        </ol>
      </nav>
    );
  }

  // ------------------------------------------------------------------ vertical
  if (variant === 'vertical') {
    return (
      <nav aria-label={ariaLabel ?? tx('stepper.label', 'Steps')} className={cx('w-full', className)} dir={dir}>
        {heading}
        <ol className="flex flex-col" dir={dir}>
          {steps.map((step, index) => {
            const status = statusOf(index);
            const isLast = index === total - 1;
            return (
              <li key={step.id} className="flex gap-3" aria-current={index === current ? 'step' : undefined}>
                <div className="flex flex-col items-center">
                  {renderMarker(step, index)}
                  {!isLast && (
                    <span
                      aria-hidden="true"
                      className={cx('my-1 min-h-[1.5rem] w-0.5 flex-1 rounded-full', index < current ? TEAL : SOFT)}
                    />
                  )}
                </div>
                <div className={cx('min-w-0', !isLast && 'pb-6')} aria-hidden="true">
                  {showLabels && step.label && (
                    <span
                      className={cx(
                        'block text-[0.9375rem] leading-snug',
                        status === 'current'
                          ? 'text-[var(--pv-color-text)]'
                          : status === 'completed'
                            ? 'text-[var(--pv-color-primary)]'
                            : 'text-[var(--pv-color-text-muted)]',
                      )}
                    >
                      {step.label}
                    </span>
                  )}
                  {showLabels && step.description && (
                    <span className="mt-0.5 block text-[0.875rem] leading-snug text-[var(--pv-color-text-muted)]">
                      {step.description}
                    </span>
                  )}
                </div>
                <span className="sr-only">{describe(step, index)}</span>
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }

  // ------------------------------------------------------------------ default
  return (
    <nav aria-label={ariaLabel ?? tx('stepper.label', 'Steps')} className={cx('w-full', className)} dir={dir}>
      {heading}
      <DefaultTrack
        steps={steps}
        current={current}
        dir={dir}
        renderMarker={renderMarker}
        describe={describe}
        label={showLabels ? currentLabel : undefined}
      />
    </nav>
  );
}

/**
 * The horizontal track plus the current-step label pill. The pill is centred under
 * its marker, then clamped so a long label on the first or last step stays inside
 * the track instead of spilling off the screen.
 */
function DefaultTrack({
  steps,
  current,
  dir,
  renderMarker,
  describe,
  label,
}: {
  steps: Step[];
  current: number;
  dir: 'ltr' | 'rtl';
  renderMarker: (step: Step, index: number) => ReactNode;
  describe: (step: Step, index: number) => string;
  label?: string;
}) {
  const total = steps.length;
  const trackRef = useRef<HTMLOListElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const [offset, setOffset] = useState<number | null>(null);

  useLayoutEffect(() => {
    const track = trackRef.current;
    const pill = pillRef.current;
    if (!track || !pill || current < 0 || current >= total) return undefined;
    const measure = () => {
      const width = track.clientWidth;
      const pillWidth = pill.offsetWidth;
      if (!width || !pillWidth) {
        setOffset(null);
        return;
      }
      const centre = ((current + 0.5) / total) * width;
      setOffset(Math.round(Math.max(0, Math.min(centre - pillWidth / 2, width - pillWidth))));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    observer.observe(pill);
    return () => observer.disconnect();
  }, [current, total, label]);

  // Before measurement (or without layout), centre with a transform.
  const pillStyle: CSSProperties =
    offset === null
      ? {
          marginInlineStart: `${((current + 0.5) / total) * 100}%`,
          transform: `translateX(${dir === 'rtl' ? 50 : -50}%)`,
        }
      : { marginInlineStart: offset };

  return (
    <>
      <ol ref={trackRef} className="flex w-full items-start" dir={dir}>
        {steps.map((step, index) => {
          const first = index === 0;
          const last = index === total - 1;
          const before = first ? 'bg-transparent' : index <= current ? TEAL : SOFT;
          const after = last ? 'bg-transparent' : index < current ? TEAL : SOFT;
          return (
            <li
              key={step.id}
              className="flex min-w-0 flex-1 flex-col items-center"
              aria-current={index === current ? 'step' : undefined}
            >
              <div className="flex w-full items-center">
                <span aria-hidden="true" data-connector="before" className={cx('h-0.5 flex-1', before)} />
                {renderMarker(step, index)}
                <span aria-hidden="true" data-connector="after" className={cx('h-0.5 flex-1', after)} />
              </div>
              <span className="sr-only">{describe(step, index)}</span>
            </li>
          );
        })}
      </ol>
      {label && current >= 0 && current < total && (
        <div className="mt-2 flex" aria-hidden="true">
          <span
            ref={pillRef}
            data-stepper-label=""
            style={pillStyle}
            className="max-w-full shrink-0 truncate rounded-full bg-[var(--pv-color-surface-sunken)] px-3 py-1 text-[0.875rem] leading-5 text-[var(--pv-color-primary)]"
          >
            {label}
          </span>
        </div>
      )}
    </>
  );
}

/** Plain «الخطوة 2 من 5» text, for places that want the count without the track. */
export function StepCounter({
  current,
  total,
  label,
  className = '',
}: {
  current: number;
  total: number;
  label?: string;
  className?: string;
}) {
  const tx = useUiText();
  return (
    <div className={cx('inline-flex items-center gap-2 text-[0.875rem]', className)}>
      {label && <span className="text-[var(--pv-color-text-muted)]">{label}</span>}
      <span className="font-medium text-[var(--pv-color-text)]">
        {tx('stepper.counter', 'Step {{current}} of {{total}}', { current, total })}
      </span>
    </div>
  );
}
